import { FLEX_EPSILON, SIZE_FREEZE_EPSILON } from "./usePanelResize.constants";
import type { ParsedConstraint } from "./usePanelResize.constraints";

/**
 * Checks whether a panel has an explicit CSS constraint in the direction that
 * would block its flex change, using getComputedStyle.
 *
 * - Flex increased (panel wants to grow) → blocked by max-height/max-width
 * - Flex decreased (panel wants to shrink) → blocked by explicit min-height/min-width
 *   (not "auto" or "0px", which are default flex behavior, not CSS caps)
 */
function hasCssConstraintInDirection(
  panel: HTMLElement,
  flexIncreased: boolean,
  isVertical: boolean
): boolean {
  const style = getComputedStyle(panel);
  if (flexIncreased) {
    const max = isVertical ? style.maxHeight : style.maxWidth;
    return max !== "none";
  }
  const min = isVertical ? style.minHeight : style.minWidth;
  return min !== "0px" && min !== "auto";
}

/**
 * Detects whether an external CSS property (max-height, min-height, etc.)
 * is preventing a panel from responding to its flex change.
 *
 * Two-stage check per panel:
 * 1. Size didn't respond to flex change (flex delta > FLEX_EPSILON, size delta < SIZE_FREEZE_EPSILON)
 * 2. Panel actually has a CSS constraint in the relevant direction (getComputedStyle)
 *
 * Stage 2 prevents false positives from flex-basis:auto layouts with zero free space,
 * where flex changes produce no size change but no CSS constraint is involved.
 */
export function isCssSizeFrozen(
  prevFirstFlex: number,
  prevSecondFlex: number,
  newFirstFlex: number,
  newSecondFlex: number,
  preFirstSize: number,
  preSecondSize: number,
  postFirstSize: number,
  postSecondSize: number,
  firstPanel: HTMLElement,
  secondPanel: HTMLElement,
  isVertical: boolean
): boolean {
  const firstSizeFroze =
    Math.abs(newFirstFlex - prevFirstFlex) > FLEX_EPSILON &&
    Math.abs(postFirstSize - preFirstSize) < SIZE_FREEZE_EPSILON;

  const secondSizeFroze =
    Math.abs(newSecondFlex - prevSecondFlex) > FLEX_EPSILON &&
    Math.abs(postSecondSize - preSecondSize) < SIZE_FREEZE_EPSILON;

  if (!firstSizeFroze && !secondSizeFroze) return false;

  const firstCapped =
    firstSizeFroze &&
    hasCssConstraintInDirection(
      firstPanel,
      newFirstFlex > prevFirstFlex,
      isVertical
    );

  const secondCapped =
    secondSizeFroze &&
    hasCssConstraintInDirection(
      secondPanel,
      newSecondFlex > prevSecondFlex,
      isVertical
    );

  return firstCapped || secondCapped;
}

/**
 * Adjusts flex pair after measuring rendered panel sizes when pixel constraints apply.
 * Caller applies flex to DOM first, measures, then calls this with sizes and parsed constraints.
 */
export function applyPixelConstraintsToFlexPair(
  newFirstFlex: number,
  newSecondFlex: number,
  firstPanelSize: number,
  secondPanelSize: number,
  firstMinVal: ParsedConstraint | null,
  firstMaxVal: ParsedConstraint | null,
  secondMinVal: ParsedConstraint | null,
  secondMaxVal: ParsedConstraint | null,
  minFlex: number
): { first: number; second: number; needsReapply: boolean } {
  let totalFlex = newFirstFlex + newSecondFlex;
  let needsReapply = false;
  let nf = newFirstFlex;
  let ns = newSecondFlex;

  if (firstPanelSize >= 1) {
    if (firstMinVal?.isPixel && firstPanelSize < firstMinVal.value) {
      const ratio = firstMinVal.value / firstPanelSize;
      nf = ratio * nf;
      ns = Math.max(totalFlex - nf, minFlex);
      needsReapply = true;
    } else if (firstMaxVal?.isPixel && firstPanelSize > firstMaxVal.value) {
      const ratio = firstMaxVal.value / firstPanelSize;
      nf = ratio * nf;
      ns = Math.max(totalFlex - nf, minFlex);
      needsReapply = true;
    }
  }

  totalFlex = nf + ns;

  if (secondPanelSize >= 1) {
    if (secondMinVal?.isPixel && secondPanelSize < secondMinVal.value) {
      const ratio = secondMinVal.value / secondPanelSize;
      ns = ratio * ns;
      nf = Math.max(totalFlex - ns, minFlex);
      needsReapply = true;
    } else if (secondMaxVal?.isPixel && secondPanelSize > secondMaxVal.value) {
      const ratio = secondMaxVal.value / secondPanelSize;
      ns = ratio * ns;
      nf = Math.max(totalFlex - ns, minFlex);
      needsReapply = true;
    }
  }

  return { first: nf, second: ns, needsReapply };
}
